// Third Party Libraries Imports
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";

// Local Imports
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";

const BASE = `/${process.env.REACT_APP_ENDPOINT_VERSION}/portfolio/new-portfolio-agreements`;

export const reviewPath = (id: string) => `${BASE}/${id}/review`;
export const previewPath = (id: string) => `${BASE}/${id}/preview`;

/** HTTP status the atomic review claim returns for a repeated/concurrent decision. */
export const ALREADY_DECIDED = 409;

export interface ReviewDecision {
  approved: boolean;
  /** Required and nonblank when rejecting. */
  feedback?: string;
  /** ISO date, required on approval when the record has no uploaded date. */
  agreementDate?: string;
}

export type DecisionOutcome = "success" | "conflict" | "error";

/**
 * Unwraps this API's `{ data: <payload> }` envelope, tolerating payloads that
 * arrive at the top level instead.
 */
const unwrap = (body: any): any => {
  if (body && typeof body === "object" && "data" in body) {
    const inner = body.data;
    if (inner && typeof inner === "object") return inner;
  }
  return body;
};

/** The preview's base64 could be keyed several ways; take the first that fits. */
const base64Of = (body: any): string | undefined => {
  const candidates = [
    body?.file,
    body?.data?.file,
    body?.pdf,
    body?.data?.pdf,
    typeof body?.data === "string" ? body.data : undefined,
    typeof body === "string" ? body : undefined,
  ];
  return candidates.find((v) => typeof v === "string" && v.length > 0);
};

const apiMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

class AgreementReviewService {
  private dispatch: any;

  constructor(dispatch?: any) {
    this.dispatch = dispatch;
  }

  private setLoading = (loading: boolean) => {
    if (this.dispatch) this.dispatch({ type: LOADING, payload: loading });
  };

  /** `GET …/:id/review` — the details the WM decides on. */
  public async getReview(id: string): Promise<any | undefined> {
    this.setLoading(true);
    try {
      const response: AxiosResponse<any> = await instance.get(reviewPath(id));
      return unwrap(response.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(apiMessage(error, "Failed to load the review"));
      }
      localStorage.setItem("star_prev_url", `/portfolio/agreement-review/${id}`);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * `GET …/:id/preview` — generates the PDF as base64 without storing or
   * distributing it.
   *
   * `params` carries the applicable date (`portfolioOpenDate` or
   * `agreementDate`) so the document reflects a date the WM has changed but
   * not yet committed. Regenerating is free of side effects, which is what
   * makes previewing an uncommitted date safe.
   *
   * Returned as a `blob:` object URL rather than a `data:` URL on purpose:
   * `PdfViewer` HEAD-checks any non-`blob:` URL and **logs the user out** when
   * that check fails, which a data URL would trigger.
   */
  public async getPreviewUrl(
    id: string,
    params: Record<string, string> = {}
  ): Promise<string | undefined> {
    this.setLoading(true);
    try {
      const response: AxiosResponse<any> = await instance.get(previewPath(id), {
        params,
      });
      const base64 = base64Of(response.data);
      if (!base64) {
        const keys =
          response.data && typeof response.data === "object"
            ? Object.keys(response.data).join(", ")
            : typeof response.data;
        toast.error(`The preview returned no document (response had: ${keys}).`);
        return undefined;
      }
      const clean = base64.includes(",")
        ? base64.slice(base64.indexOf(",") + 1)
        : base64;
      const binary = atob(clean.replace(/\s/g, ""));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(apiMessage(error, "Failed to generate the preview"));
      }
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * `POST …/:id/review` — one endpoint for both outcomes via `approved`.
   *
   * A 409 means the atomic status claim was already taken: someone else
   * decided, or this decision was submitted twice. That is reported as its own
   * outcome so the caller can refresh rather than show a generic failure.
   */
  public async decide(
    id: string,
    decision: ReviewDecision
  ): Promise<DecisionOutcome> {
    this.setLoading(true);
    try {
      await instance.post(reviewPath(id), decision);
      toast.success(
        decision.approved
          ? "Agreement approved successfully"
          : "Agreement returned to the client"
      );
      return "success";
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === ALREADY_DECIDED) {
        toast.error(
          apiMessage(
            error,
            "This request has already been decided. Reloading the latest state."
          )
        );
        return "conflict";
      }
      if (status !== 401) {
        toast.error(apiMessage(error, "Failed to submit the decision"));
      }
      return "error";
    } finally {
      this.setLoading(false);
    }
  }
}

export default AgreementReviewService;
